import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { AlertTriangle, ArrowDown, ArrowUp, Check, Copy, Download, Package, Plus, Search, SlidersHorizontal, Upload } from "lucide-react";
import { api, apiError, downloadCsv, money, STATUS, todayISO } from "@/lib/api";
import { useSettings } from "@/context/SettingsContext";
import { useAuth } from "@/context/AuthContext";
import Layout from "@/components/Layout";
import StickyFilters from "@/components/StickyFilters";
import { useTenant } from "@/context/TenantContext";
import { cacheGet, cacheKey, cacheSet } from "@/lib/cache";
import ItemDrawer from "@/components/ItemDrawer";
import SelectionBar from "@/components/SelectionBar";
import ImportDialog from "@/components/ImportDialog";
import { EmptyState, ItemCode, LoadingPanel, StatusBadge } from "@/components/Bits";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { cn } from "@/lib/utils";

// Every column is shown on every screen. Long text truncates and the spacing
// tightens rather than a column disappearing — somebody who only ever uses a
// laptop shouldn't be missing a figure they don't know exists.
const COLUMNS = [
  { key: "item_code", label: "Code", sortable: true },
  { key: "name", label: "Item", sortable: true },
  { key: "category", label: "Category", sortable: true },
  { key: "unit_cost", label: "Cost", sortable: true, align: "right" },
  { key: "selling_price", label: "Price", sortable: true, align: "right" },
  { key: "current_stock", label: "On hand", sortable: true, align: "right" },
  { key: "stock_value", label: "Value", sortable: true, align: "right" },
  { key: "status", label: "Status", sortable: true },
];

const BLANK = {
  name: "",
  category: "",
  unit_cost: "",
  selling_price: "",
  opening_qty: "",
  acquisition_date: todayISO(),
  notes: "",
};

function NewItemDialog({ open, onOpenChange, categories, markup, onCreated }) {
  const [form, setForm] = useState(BLANK);
  const [preview, setPreview] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (open) {
      setForm(BLANK);
      setPreview("");
    }
  }, [open]);

  // Show the code the item will get before it's saved.
  useEffect(() => {
    if (!form.category) {
      setPreview("");
      return;
    }
    let cancelled = false;
    api
      .get("/categories/next-code", { params: { category: form.category } })
      .then(({ data }) => !cancelled && setPreview(data.item_code))
      .catch(() => !cancelled && setPreview(""));
    return () => {
      cancelled = true;
    };
  }, [form.category]);

  const suggestedPrice = useMemo(() => {
    const cost = Number(form.unit_cost);
    return cost > 0 ? Math.round(cost * markup * 100) / 100 : "";
  }, [form.unit_cost, markup]);

  const submit = async () => {
    if (!form.name.trim() || !form.category) {
      toast.error("Give the item a name and a category");
      return;
    }
    setBusy(true);
    try {
      await api.post("/items", {
        name: form.name,
        category: form.category,
        acquisition_date: form.acquisition_date,
        unit_cost: Number(form.unit_cost) || 0,
        selling_price:
          form.selling_price === "" ? undefined : Number(form.selling_price),
        opening_qty: Number(form.opening_qty) || 0,
        notes: form.notes,
      });
      toast.success("Item added");
      onOpenChange(false);
      onCreated();
    } catch (err) {
      toast.error(apiError(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[540px]">
        <DialogHeader>
          <DialogTitle className="font-display text-xl">Add an item</DialogTitle>
          <DialogDescription>
            The code is generated from the category prefix. Leave the price blank to
            use your {markup}× markup.
          </DialogDescription>
        </DialogHeader>

        <div className="grid sm:grid-cols-2 gap-4 py-1">
          <div className="space-y-1.5 sm:col-span-2">
            <Label htmlFor="iname">Item name</Label>
            <Input
              id="iname"
              value={form.name}
              onChange={(e) => setForm({ ...form, name: e.target.value })}
              placeholder="What you call it"
            />
          </div>

          <div className="space-y-1.5">
            <Label>Category</Label>
            <Select
              value={form.category}
              onValueChange={(v) => setForm({ ...form, category: v })}
            >
              <SelectTrigger>
                <SelectValue placeholder="Choose one" />
              </SelectTrigger>
              <SelectContent>
                {categories.map((c) => (
                  <SelectItem key={c.category_id || c.name} value={c.name}>
                    <span className="font-mono text-gold mr-2">{c.prefix}</span>
                    {c.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="space-y-1.5">
            <Label>Item code</Label>
            <div className="h-10 flex items-center px-3 rounded-md border border-line bg-surface">
              {preview ? (
                <ItemCode code={preview} />
              ) : (
                <span className="text-[13px] text-muted2">Pick a category first</span>
              )}
            </div>
          </div>

          <div className="space-y-1.5">
            <Label htmlFor="cost">Unit cost</Label>
            <Input
              id="cost"
              type="number"
              min="0"
              value={form.unit_cost}
              onChange={(e) => setForm({ ...form, unit_cost: e.target.value })}
              placeholder="0"
            />
          </div>

          <div className="space-y-1.5">
            <Label htmlFor="price">Selling price</Label>
            <Input
              id="price"
              type="number"
              min="0"
              value={form.selling_price}
              onChange={(e) => setForm({ ...form, selling_price: e.target.value })}
              placeholder={suggestedPrice ? String(suggestedPrice) : "Auto"}
            />
          </div>

          <div className="space-y-1.5">
            <Label htmlFor="qty">Opening quantity</Label>
            <Input
              id="qty"
              type="number"
              min="0"
              value={form.opening_qty}
              onChange={(e) => setForm({ ...form, opening_qty: e.target.value })}
              placeholder="0"
            />
          </div>

          <div className="space-y-1.5">
            <Label htmlFor="acq">Acquired</Label>
            <Input
              id="acq"
              type="date"
              value={form.acquisition_date}
              onChange={(e) => setForm({ ...form, acquisition_date: e.target.value })}
            />
          </div>
        </div>

        <DialogFooter>
          <Button variant="ghost" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button onClick={submit} disabled={busy} className="bg-brand hover:bg-brand-hover">
            Add item
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export default function Inventory() {
  const { user } = useAuth();
  // Merging removes item records, so it stays with admins.
  const canAdmin = ["owner", "admin"].includes(user?.role);
  const { settings, categories } = useSettings();
  const sym = settings.currency_symbol;
  const { tenantId } = useTenant();
  const CK = cacheKey("items", tenantId);
  // Seed from whatever was last seen so returning to this page is instant.
  const [items, setItems] = useState(() => cacheGet(CK) || []);
  const [loading, setLoading] = useState(() => !cacheGet(CK));
  const [query, setQuery] = useState("");
  const [category, setCategory] = useState("all");
  const [status, setStatus] = useState("all");
  const [sort, setSort] = useState({ key: "item_code", dir: "asc" });
  const [adding, setAdding] = useState(false);
  const [openCode, setOpenCode] = useState(null);
  const [importing, setImporting] = useState(false);
  const [selected, setSelected] = useState([]);
  const [merging, setMerging] = useState(false);
  const [mergeInto, setMergeInto] = useState("");
  const [searchParams, setSearchParams] = useSearchParams();

  // Search results link here with ?item=CODE — open that item straight away.
  // Mutating the params object in place and writing it back re-triggers this
  // effect, so build a fresh one and only act when the code is actually there.
  const itemParam = searchParams.get("item");
  useEffect(() => {
    if (!itemParam) return;
    setOpenCode(itemParam);
    const next = new URLSearchParams(window.location.search);
    next.delete("item");
    setSearchParams(next, { replace: true });
  }, [itemParam, setSearchParams]);

  // Ignore a reply that lands after the client has been switched — otherwise
  // one business's figures can appear under another's name.
  const currentKey = useRef(CK);
  useEffect(() => {
    currentKey.current = CK;
  }, [CK]);

  const load = useCallback(async () => {
    const askedFor = CK;
    // Only show the skeleton when there's nothing to show yet; otherwise this
    // is a quiet background refresh.
    if (!cacheGet(CK)) setLoading(true);
    try {
      const { data } = await api.get("/items");
      if (currentKey.current !== askedFor) return;
      setItems(data);
      cacheSet(CK, data);
    } catch (err) {
      toast.error(apiError(err));
    } finally {
      setLoading(false);
    }
  }, [CK]);

  useEffect(() => {
    load();
  }, [load]);

  const visible = useMemo(() => {
    const q = query.trim().toLowerCase();
    let rows = items.filter((i) => {
      if (category !== "all" && i.category !== category) return false;
      if (status !== "all" && i.status !== status) return false;
      if (!q) return true;
      return (
        i.item_code.toLowerCase().includes(q) ||
        i.name.toLowerCase().includes(q) ||
        (i.category || "").toLowerCase().includes(q)
      );
    });
    const { key, dir } = sort;
    rows = [...rows].sort((a, b) => {
      const av = a[key] ?? "";
      const bv = b[key] ?? "";
      const cmp =
        typeof av === "number" && typeof bv === "number"
          ? av - bv
          : String(av).localeCompare(String(bv), undefined, { numeric: true });
      return dir === "asc" ? cmp : -cmp;
    });
    return rows;
  }, [items, query, category, status, sort]);

  /**
   * Updating one item shouldn't blank the whole table. When the drawer hands
   * back the saved item we patch that row in place; a full reload is only
   * needed when something was deleted.
   */
  // Worked out from the list already on screen rather than fetched separately.
  // Renaming an item patches its row in place, so a flag that came from its own
  // request would sit stale until the next reload.
  const flagged = useMemo(() => {
    const seen = new Map();
    items.forEach((i) => {
      const key = `${(i.name || "").trim().toLowerCase()}|${(i.category || "").trim().toLowerCase()}`;
      seen.set(key, [...(seen.get(key) || []), i.item_code]);
    });
    const set = new Set();
    seen.forEach((codes) => {
      if (codes.length > 1) codes.forEach((c) => set.add(c));
    });
    return set;
  }, [items]);

  const chosen = items.filter((i) => selected.includes(i.item_code));
  const toggleSel = (code) =>
    setSelected((cur) => (cur.includes(code) ? cur.filter((x) => x !== code) : [...cur, code]));

  const doMerge = async () => {
    if (!mergeInto) return;
    try {
      const { data } = await api.post("/items/merge", {
        from_codes: selected.filter((c) => c !== mergeInto),
        into_code: mergeInto,
      });
      toast.success(`Merged into ${data.kept} — ${data.stock} in stock`);
      setMerging(false);
      setSelected([]);
      setMergeInto("");
      load();
    } catch (err) {
      toast.error(apiError(err));
    }
  };

  const applyItemChange = useCallback(
    (updated) => {
      if (!updated?.item_code) {
        load();
        return;
      }
      setItems((prev) => {
        const next = prev.map((i) =>
          i.item_code === updated.item_code ? { ...i, ...updated } : i
        );
        cacheSet(CK, next);
        return next;
      });
    },
    [load, CK]
  );

  const toggleSort = (key) =>
    setSort((s) => ({ key, dir: s.key === key && s.dir === "asc" ? "desc" : "asc" }));

  const summary = useMemo(() => {
    const value = visible.reduce((t, i) => t + Math.max(i.stock_value, 0), 0);
    const units = visible.reduce((t, i) => t + Math.max(i.current_stock, 0), 0);
    return { value, units };
  }, [visible]);

  const filtered = query || category !== "all" || status !== "all";

  return (
    <Layout
      title="Inventory"
      subtitle={
        // Held back until the data arrives — otherwise it briefly
        // describes an empty page.
        loading ? undefined : `${visible.length} of ${items.length} items · ${summary.units} units · ${money(
        summary.value,
        sym
      )} at cost`
      }
      action={
        <>
          <Button
            variant="outline"
            onClick={() => downloadCsv("/export/items.csv", "inventory-master.csv")}
            className="hidden sm:inline-flex"
          >
            <Download className="w-4 h-4 mr-2" /> Export
          </Button>
          <Button onClick={() => setAdding(true)} className="bg-brand hover:bg-brand-hover">
            <Plus className="w-4 h-4 sm:mr-2" />
            <span className="hidden sm:inline">Add item</span>
          </Button>
        </>
      }
    >
      <StickyFilters>
        <div className="flex flex-col sm:flex-row gap-2.5">
        <div className="relative flex-1 min-w-0">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted2" />
          <Input
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Search by code, name or category"
            className="pl-9 border-line"
          />
        </div>
        <Select value={category} onValueChange={setCategory}>
          <SelectTrigger className="sm:w-[190px] border-line">
            <SlidersHorizontal className="w-3.5 h-3.5 mr-1.5 text-muted2 shrink-0" />
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All categories</SelectItem>
            {categories.map((c) => (
              <SelectItem key={c.category_id || c.name} value={c.name}>
                {c.name}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        <Select value={status} onValueChange={setStatus}>
          <SelectTrigger className="sm:w-[180px] border-line">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Any status</SelectItem>
            <SelectItem value={STATUS.IN}>In stock</SelectItem>
            <SelectItem value={STATUS.LOW}>Repurchase needed</SelectItem>
            <SelectItem value={STATUS.OUT}>Out of stock</SelectItem>
          </SelectContent>
        </Select>
        </div>
      </StickyFilters>

      <SelectionBar
        count={selected.length}
        detail={`${chosen.reduce((n, i) => n + i.current_stock, 0)} units between them`}
        onClear={() => setSelected([])}
        onAction={() => {
          setMergeInto(selected[0]);
          setMerging(true);
        }}
      />

      {loading ? (
        <LoadingPanel rows={8} />
      ) : visible.length === 0 ? (
        <div className="panel">
          <EmptyState
            icon={Package}
            title={filtered ? "Nothing matches those filters" : "No items yet"}
            body={
              filtered
                ? "Try a different search, or clear the filters to see everything."
                : "Add items one at a time, or import a spreadsheet you already have."
            }
            action={
              filtered ? (
                <Button
                  variant="outline"
                  onClick={() => {
                    setQuery("");
                    setCategory("all");
                    setStatus("all");
                  }}
                >
                  Clear filters
                </Button>
              ) : (
                <div className="flex flex-col sm:flex-row gap-2.5">
                  <Button onClick={() => setAdding(true)} className="bg-brand hover:bg-brand-hover">
                    <Plus className="w-4 h-4 mr-2" /> Add item
                  </Button>
                  <Button variant="outline" onClick={() => setImporting(true)}>
                    <Upload className="w-4 h-4 mr-2" /> Import a spreadsheet
                  </Button>
                </div>
              )
            }
          />
        </div>
      ) : (
        <>

        <div className="panel overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full min-w-[560px]">
              <thead>
                <tr className="border-b border-line bg-surface/60">
                  {canAdmin && <th className="w-8" />}
                  {COLUMNS.map((c) => (
                    <th
                      key={c.key}
                      className={cn(
                        "px-2 lg:px-3 xl:px-4 py-3 text-left whitespace-nowrap",
                        c.align === "right" && "text-right"
                      )}
                    >
                      <button
                        onClick={() => c.sortable && toggleSort(c.key)}
                        className={cn(
                          "t-head inline-flex items-center gap-1 hover:text-ink transition-colors",
                          c.align === "right" && "flex-row-reverse"
                        )}
                      >
                        {c.label}
                        {sort.key === c.key &&
                          (sort.dir === "asc" ? (
                            <ArrowUp className="w-3 h-3 text-brand" />
                          ) : (
                            <ArrowDown className="w-3 h-3 text-brand" />
                          ))}
                      </button>
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {visible.map((i) => (
                  <tr
                    key={i.item_code}
                    onClick={() => setOpenCode(i.item_code)}
                    className={cn(
                      "t-row cursor-pointer last:border-0",
                      selected.includes(i.item_code) && "bg-brand-light/40"
                    )}
                  >
                    {canAdmin && (
                      <td className="t-cell w-8" onClick={(e) => e.stopPropagation()}>
                        <button
                          onClick={() => toggleSel(i.item_code)}
                          aria-label={`Select ${i.name}`}
                          className={cn(
                            "w-[16px] h-[16px] rounded-full border flex items-center justify-center transition-colors",
                            selected.includes(i.item_code)
                              ? "bg-brand border-brand"
                              : "border-line hover:border-brand-ring"
                          )}
                        >
                          {selected.includes(i.item_code) && (
                            <Check className="w-2.5 h-2.5 text-white" />
                          )}
                        </button>
                      </td>
                    )}
                    <td className="t-cell">
                      <ItemCode code={i.item_code} />
                    </td>
                    <td className="t-cell font-medium max-w-[160px] lg:max-w-[220px] xl:max-w-[300px] truncate">
                      <span className="inline-flex items-center gap-1.5">
                        {i.name}
                        {flagged.has(i.item_code) && (
                          <span
                            title="Another item shares this name and category"
                            className="inline-flex items-center gap-1 text-[10.5px] text-gold bg-gold-light rounded-full px-1.5 py-0.5 shrink-0"
                          >
                            <Copy className="w-2.5 h-2.5" />
                            dupe
                          </span>
                        )}
                      </span>
                    </td>
                    <td className="t-cell text-muted2 max-w-[150px] truncate">{i.category}</td>
                    <td className="t-cell num text-muted2">{money(i.unit_cost, sym)}</td>
                    <td className="t-cell num">{money(i.selling_price, sym)}</td>
                    <td
                      className={cn(
                        "t-cell num font-mono font-medium",
                        i.current_stock <= 0
                          ? "text-stock-out"
                          : i.current_stock <= settings.low_stock_threshold
                          ? "text-stock-low"
                          : "text-ink"
                      )}
                    >
                      {i.current_stock}
                    </td>
                    <td className="t-cell num text-muted2">{money(i.stock_value, sym)}</td>
                    <td className="t-cell">
                      <StatusBadge status={i.status} />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
        </>
      )}

      <NewItemDialog
        open={adding}
        onOpenChange={setAdding}
        categories={categories}
        markup={settings.markup_multiplier}
        onCreated={load}
      />

      <Dialog open={merging} onOpenChange={(v) => !v && setMerging(false)}>
        <DialogContent className="sm:max-w-[440px] max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-display text-xl">
              Merge {chosen.length} items into one
            </DialogTitle>
            <DialogDescription>
              Stock is pooled and every purchase, sale and gift box entry moves to
              the one you keep.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-1">
            <div className="space-y-2">
              <Label>Keep</Label>
              <div className="space-y-1.5 max-h-[220px] overflow-y-auto">
                {chosen.map((i) => (
                  <button
                    key={i.item_code}
                    onClick={() => setMergeInto(i.item_code)}
                    className={cn(
                      "w-full text-left rounded-xl border px-3.5 py-2.5 transition-colors flex items-center gap-3",
                      mergeInto === i.item_code
                        ? "border-brand bg-brand-light"
                        : "border-line hover:bg-brand-light/40"
                    )}
                  >
                    <span
                      className={cn(
                        "w-[15px] h-[15px] rounded-full border shrink-0",
                        mergeInto === i.item_code ? "bg-brand border-brand" : "border-line"
                      )}
                    />
                    <span className="font-mono text-[11.5px] text-ink shrink-0">
                      {i.item_code}
                    </span>
                    <span className="text-[12px] text-muted2 flex-1 truncate">
                      {money(i.unit_cost, sym)} / {money(i.selling_price, sym)}
                    </span>
                    <span className="font-mono text-[12px] text-muted2">
                      {i.current_stock}
                    </span>
                  </button>
                ))}
              </div>
            </div>

            <div className="rounded-xl border border-line overflow-hidden">
              <div className="px-4 py-3 bg-surface/60 space-y-2 text-[13px]">
                <div className="flex">
                  <span className="flex-1 text-muted2">Stock after merging</span>
                  <span className="font-mono text-ink">
                    {chosen.map((i) => i.current_stock).join(" + ")} ={" "}
                    {chosen.reduce((n, i) => n + i.current_stock, 0)}
                  </span>
                </div>
                <div className="flex">
                  <span className="flex-1 text-muted2">Kept code</span>
                  <span className="font-mono text-ink">{mergeInto || "—"}</span>
                </div>
              </div>
            </div>

            {/* Only worth saying when the numbers actually differ. */}
            {chosen.some(
              (i) =>
                i.unit_cost !== chosen[0].unit_cost ||
                i.selling_price !== chosen[0].selling_price
            ) && (
              <div className="rounded-xl bg-gold-light px-3.5 py-2.5 flex gap-2.5">
                <AlertTriangle className="w-4 h-4 text-gold shrink-0 mt-0.5" />
                <p className="text-[12px] text-gold leading-relaxed">
                  These don't all have the same cost and price. The one you keep
                  decides both.
                </p>
              </div>
            )}

            <div className="rounded-xl bg-rose-50 px-3.5 py-2.5 flex gap-2.5">
              <AlertTriangle className="w-4 h-4 text-stock-out shrink-0 mt-0.5" />
              <p className="text-[12px] text-stock-out leading-relaxed">
                {selected.filter((c) => c !== mergeInto).join(", ")} will no longer
                exist. This can't be undone.
              </p>
            </div>
          </div>

          <DialogFooter>
            <Button variant="ghost" onClick={() => setMerging(false)}>
              Cancel
            </Button>
            <Button
              onClick={doMerge}
              disabled={!mergeInto}
              className="bg-brand hover:bg-brand-hover"
            >
              Merge into one
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <ItemDrawer
        onRecoded={(from, to) => {
          // Keep the drawer pointing at the item under its new code, and drop
          // the old row so the list doesn't show a code that's gone.
          setOpenCode(to);
          setItems((prev) => prev.filter((i) => i.item_code !== from));
          load();
        }}
        itemCode={openCode}
        open={!!openCode}
        onOpenChange={(v) => !v && setOpenCode(null)}
        onChanged={applyItemChange}
      />
      <ImportDialog open={importing} onOpenChange={setImporting} onImported={load} />
    </Layout>
  );
}
